import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Search as SearchIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import AppMessage from "../../components/common/AppMessage";
import Loader from "../../components/common/Loader";
import KycUploadModal from "../../components/buyer/ProfileOverview/KycUploadModal";
import { fetchUserKycList, uploadUserKyc } from "../../features/user/userSlice";
import { useNavigate } from "react-router-dom";

// ✅ Your custom theme
const customTheme = createTheme({
  palette: {
    primary: { main: "#1976d2" },
    secondary: { main: "#ff9800", contrastText: "#fff" },
    scraptradeYellow: { main: "#F4C542", dark: "#E3AE27" },
    scraptradeDark: { main: "#2D2D2D" },
    background: { default: "#f4f6f8" },
  },
  typography: { fontFamily: "'Inter', sans-serif" },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { borderRadius: "16px" } } },
  },
});

const KycVerificationPageContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { kycLoading } = useSelector((state) => state.user);
  const documents = useSelector((state) => state.user.kycList) || [];
  console.log("documents:", documents);

  const warning = useSelector((state) => state.ui.kycWarningMessage);

  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openKYCModal, setOpenKYCModal] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(fetchUserKycList());
  }, [dispatch]);

  const getDocumentsFromItem = (item) => {
    const docs = [];

    for (let i = 1; i <= 4; i++) {
      const type = item[`document_type${i}`];
      const url = item[`document_url${i}`];

      if (type || url) {
        docs.push({ type, url });
      }
    }

    return docs;
  };

  const filteredDocs = documents.filter(
    (doc) =>
      (doc.kyc_type || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.status || "").toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const startIndex = (page - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentDocs = filteredDocs.slice(startIndex, endIndex);
  console.log("current Docs:", currentDocs);
  const totalPages = Math.ceil(filteredDocs.length / entriesPerPage);

  const getStatusChipColor = (status = "") => {
    switch (status.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleSaveKYC = async (data) => {
    try {
      const formData = new FormData();
      formData.append("name", data.name);
      // formData.append("is_consent", data.isConsent ? 1 : 0);
      formData.append("is_consent", 0);

      data.documents.forEach((doc, index) => {
        formData.append(`kyc[${index}][kycType]`, data.kycType);
        formData.append(`kyc[${index}][documentType]`, doc.type);
        formData.append(`kyc[${index}][file]`, doc.file);
      });

      await dispatch(uploadUserKyc(formData)).unwrap();
      dispatch(fetchUserKycList());
      setOpenKYCModal(false);
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const CustomPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Button
          key={i}
          variant={page === i ? "contained" : "outlined"}
          size="small"
          onClick={() => setPage(i)}
          sx={{
            minWidth: { xs: 28, sm: 36 },
            height: { xs: 28, sm: 32 },
            mx: { xs: 0.15, sm: 0.3 },
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            fontWeight: "bold",
            borderRadius: "4px",
            bgcolor: page === i ? "grey.300" : "#fff",
            border: "1px solid #ccc",
            color: "#000",
            "&:hover": { bgcolor: page === i ? "grey.400" : "grey.100" },
          }}
        >
          {i}
        </Button>,
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 0.5, sm: 1 },
          mt: 1,
        }}
      >
        <Button
          size="small"
          onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          disabled={page === 1}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: { xs: 28, sm: 32 },
            minWidth: { xs: 40, sm: 60 },
            bgcolor: "#fff",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          Prev
        </Button>

        {pages}

        <Button
          size="small"
          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            border: "1px solid #ccc",
            borderRadius: "4px",
            height: { xs: 28, sm: 32 },
            minWidth: { xs: 40, sm: 60 },
            bgcolor: "#fff",
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          Next
        </Button>
      </Box>
    );
  };

  const documentOptions = [
  { value: "company_registration", label: "Company Registration (ABN/ACN)" },
  { value: "sled_certificate", label: "SLED – Scrap Trader Registration Certificate" },
  { value: "driver_license", label: "Driver's License" },
  { value: "address_proof", label: "Address Proof (Utility Bill, Bank Statement, Credit Card Statement)" },
  { value: "other", label: "Others" },
];

const getDocumentLabel = (value) => {
  const option = documentOptions.find((opt) => opt.value === value);
  return option ? option.label : value;
};


  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh", // ✅ ensures full screen height
        display: "flex",
        flexDirection: "column",
        backgroundColor: theme.palette.background.default,
        p: { xs: 2, sm: 3 },
      }}
    >
      {warning && (
        <AppMessage
          type="error"
          message="Complete your profile verification for seamless access."
          style={{ marginBottom: 12 }}
        />
      )}

      <Typography
        variant={isMobile ? "h5" : "h4"}
        sx={{ fontWeight: "bold", mb: 1 }}
      >
        Verify Your Profile
      </Typography>

      <Typography
        variant={isMobile ? "body1" : "h6"}
        color="text.secondary"
        sx={{ mb: 3 }}
      >
       Please upload your documents here to complete your Profile Verification (Company Registration – ABN/ACN, SLED – Scrap Trader Registration Certificate, Driver’s License, Address Proof – Utility Bill, Bank Statement, or Credit Card Statement).
      </Typography>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Uploaded Documents
        </Typography>
        <Button
          variant="contained"
          sx={{
            bgcolor: "scraptradeYellow.main",
            "&:hover": { bgcolor: "scraptradeYellow.dark" },
          }}
          onClick={() => setOpenKYCModal(true)}
        >
          Upload
        </Button>
      </Box>

      {kycLoading && <Loader />}

      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl sx={{ minWidth: { xs: "100%", sm: 150 } }}>
          <InputLabel>Entries per page</InputLabel>
          <Select
            value={entriesPerPage}
            onChange={(e) => {
              setEntriesPerPage(e.target.value);
              setPage(1);
            }}
            label="Entries per page"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
          </Select>
        </FormControl>

        <TextField
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ width: "100%", overflowX: "auto", padding: 1 }}>
        <TableContainer component={Paper}>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {[
                  "Sr. No",
                  "Verification Type",
                  "Document Type",
                  "File",
                  "Status",
                  "Uploaded",
                ].map((header, index) => (
                  <TableCell key={index} sx={{ fontWeight: "bold" }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentDocs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: "text.secondary", fontStyle: "italic" }}
                  >
                    No verification documents uploaded yet.
                  </TableCell>
                </TableRow>
              ) : (
                currentDocs.map((doc, idx) => {
                  const docs = getDocumentsFromItem(doc);

                  return (
                    <TableRow key={doc.id}>
                      <TableCell>{startIndex + idx + 1}</TableCell>

                      <TableCell>{capitalizeFirst(doc.kyc_type)}</TableCell>

                      {/* Document Types */}
                      <TableCell>
                        {docs.map((d, index) => (
                          <Box
                            key={index}
                            sx={{
                              mt: index > 0 ? 1 : 0,
                              pt: index > 0 ? 1 : 0,
                              borderTop: index > 0 ? "1px solid #eee" : "none",
                              fontWeight: 500,
                            }}
                          >
                            {getDocumentLabel(d.type) || "N/A"}
                          </Box>
                        ))}
                      </TableCell>

                      {/* File Buttons */}
                      <TableCell>
                        {docs.map((d, index) => (
                          <Box
                            key={index}
                            sx={{
                              mt: index > 0 ? 1 : 0,
                              pt: index > 0 ? 1 : 0,
                              borderTop: index > 0 ? "1px solid #eee" : "none",
                            }}
                          >
                            {d.url ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => window.open(d.url, "_blank")}
                                sx={{
                                  textTransform: "none",
                                  borderRadius: "20px",
                                  px: 2,
                                  py: 0.5,
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  bgcolor: "scraptradeYellow.main",
                                  color: "#000",
                                  "&:hover": {
                                    bgcolor: "scraptradeYellow.dark",
                                  },
                                }}
                              >
                                View
                              </Button>
                            ) : (
                              "-"
                            )}
                          </Box>
                        ))}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={capitalizeFirst(doc.status)}
                          color={getStatusChipColor(doc.status)}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>{timeAgo(doc.created_at)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mt: 2,
          gap: 2,
        }}
      >
        <Typography variant="body2">
          Showing {startIndex + 1} to {Math.min(endIndex, filteredDocs.length)}{" "}
          of {filteredDocs.length} entries
        </Typography>

        <CustomPagination />
      </Box>

      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate("/market")}
        >
          Go to Market
        </Button>

        <Button
          variant="contained"
          sx={{
            bgcolor: "scraptradeYellow.main",
            "&:hover": { bgcolor: "scraptradeYellow.dark" },
          }}
          onClick={() => navigate("/dashboard")}
        >
          Go to Dashboard
        </Button>
      </Box>

      <KycUploadModal
        open={openKYCModal}
        onClose={() => setOpenKYCModal(false)}
        onSave={handleSaveKYC}
      />
    </Box>
  );
};

// Wrap your component in the ThemeProvider here
const KycVerificationPage = () => (
  <ThemeProvider theme={customTheme}>
    <KycVerificationPageContent />
  </ThemeProvider>
);

export default KycVerificationPage;
